import { createRouter, createWebHistory, type RouteMeta } from 'vue-router'
import DashboardLayoutVue from '@/layouts/dashboard.vue';
import { createClient } from '@supabase/supabase-js'

interface IRouteMeta {
  title: string;
  requiresAuth?: boolean;
}

// Supabase client setup
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://juxlmigvbbyhcwjadqhs.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_KEY || 'your-anon-key-here'
const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Create an auth service using Supabase
const authService = {
  // Check if user is authenticated
  isAuthenticated(): boolean {
    const session = JSON.parse(localStorage.getItem('sb-juxlmigvbbyhcwjadqhs-auth-token') || '{}')
    return !!session.access_token && new Date(session.expires_at * 1000) > new Date()
  },
  
  // Login method
  async login(email: string, password: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) throw error
      
      return !!data.session
    } catch (error) {
      console.error('Login error:', error)
      return false
    }
  },
  
  // Social login method
  async socialLogin(provider: 'google' | 'github' | 'facebook'): Promise<boolean> {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin + '/dashboard/home'
        }
      })
      
      if (error) throw error
      
      // OAuth redirects to the provider, so we won't get here immediately
      return true
    } catch (error) {
      console.error(`Social login error with ${provider}:`, error)
      return false
    }
  },
  
  // Logout method
  async logout(): Promise<void> {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    } catch (error) {
      console.error('Logout error:', error)
    }
  },
  
  // Get user information
  getUserInfo() {
    const session = JSON.parse(localStorage.getItem('sb-juxlmigvbbyhcwjadqhs-auth-token') || '{}')
    return session?.user || null
  },
  
  // Check session and refresh if needed
  async checkSession(): Promise<boolean> {
    const { data, error } = await supabase.auth.getSession()
    if (error) {
      console.error('Session check error:', error)
      return false
    }
    return !!data.session
  },
  
  // Initialize auth state listener
  initAuthStateListener(callback: (session: any) => void) {
    supabase.auth.onAuthStateChange((event, session) => {
      callback(session)
    })
  }
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/dashboard/home',
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/Login.vue'),
      meta: {
        title: 'Login',
        requiresAuth: false,
      } as RouteMeta & IRouteMeta,
    },
    {
      path: '/dashboard',
      component: DashboardLayoutVue,
      redirect: '/dashboard/home',
      meta: {
        title: 'Dashboard',
        requiresAuth: true,
      } as RouteMeta & IRouteMeta,
      children: [
        {
          path: 'home',
          name: 'home',
          component: () => import('@/views/dashboard/examples/Home.vue'),
          meta: {
            title: 'Home',
            requiresAuth: true,
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'task',
          name: 'tasks_index',
          component: () => import('@/views/dashboard/examples/tasks/Index.vue'),
          meta: {
            title: 'Tasks',
            requiresAuth: true,
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'user',
          name: 'user_index',
          component: () => import('@/views/dashboard/examples/user/Index.vue'),
          meta: {
            title: 'User',
            requiresAuth: true,
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'settings',
          name: 'settings_index',
          component: () => import('@/views/dashboard/examples/settings/Index.vue'),
          meta: {
            title: 'Settings',
            requiresAuth: true,
          } as RouteMeta & IRouteMeta
        },
        {
          path: 'profile',
          name: 'profile_index',
          component: () => import('@/views/dashboard/examples/profile/Index.vue'),
          meta: {
            title: 'profile',
            requiresAuth: true,
          } as RouteMeta & IRouteMeta
        },
      ],
    },
    {
      path: '/:pathMatch(.*)',
      name: 'not-found',
      component: () => import('@/views/404.vue'),
      meta: {
        title: 'Page Not Found',
        requiresAuth: false,
      } as RouteMeta & IRouteMeta,
    },
  ]
});

// Authentication middleware
router.beforeEach(async (to, from, next) => {
  // Update document title
  document.title = to.meta.title as string;
  
  // Check if the route requires authentication
  const requiresAuth = to.matched.some(record => (record.meta as unknown as IRouteMeta).requiresAuth);
  
  // Check if user is authenticated
  const isAuthenticated = authService.isAuthenticated();
  
  // Handle OAuth callback
  if (to.hash.includes('access_token')) {
    const { data, error } = await supabase.auth.getSession();
    if (!error) {
      return next({ path: '/dashboard/home' });
    }
  }
  
  if (requiresAuth && !isAuthenticated) {
    // Redirect to login page if authentication is required but user is not authenticated
    next({ 
      name: 'login',
      // Store the path the user was trying to access so you can redirect after login
      query: { redirect: to.fullPath } 
    });
  } else if ((to.name === 'login' || to.name === 'register') && isAuthenticated) {
    // If user is already logged in and tries to access login page, redirect to dashboard
    next({ path: '/dashboard/home' });
  } else {
    // Continue to the route
    next();
  }
});

// Initialize auth state listener
authService.initAuthStateListener((session) => {
  if (!session && router.currentRoute.value.meta.requiresAuth) {
    router.push({ name: 'login' });
  }
});

export default router;

// Also export auth service for use in components
export { authService, supabase };